/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import type { UrlObject } from "url";

/**
 * Type guard to check if a value is a string
 * @param value - The value to check
 * @returns True if value is a string
 */
export function isString(value: unknown): value is string {
	return typeof value === "string";
}

/**
 * Type guard to check if a value is a plain object (Record)
 * @param value - The value to check
 * @returns True if value is a plain object
 */
export function isRecord(value: unknown): value is Record<string, unknown> {
	return value !== null && typeof value === "object" && !Array.isArray(value) && !(value instanceof Date);
}

/**
 * Type guard to check if a value is a UrlObject
 * @param value - The value to check
 * @returns True if value is a UrlObject-like object
 */
export function isUrlObject(value: unknown): value is UrlObject {
	if (!isRecord(value)) {
		return false;
	}
	// Check for UrlObject properties (pathname, href, or protocol/hostname)
	return "pathname" in value || "href" in value || "protocol" in value || "hostname" in value;
}

/**
 * Converts a UrlObject to a string (client-safe implementation)
 * @param urlObject - The UrlObject to convert
 * @returns String representation of the URL
 */
function formatUrlObject(urlObject: UrlObject): string {
	// If href is provided, use it directly
	if (urlObject.href && typeof urlObject.href === "string") {
		return urlObject.href;
	}

	// Build URL from components
	let url = "";

	// Protocol and hostname
	if (urlObject.protocol && urlObject.hostname) {
		url += `${urlObject.protocol}//`;
		if (urlObject.auth) {
			url += urlObject.auth + "@";
		}
		url += urlObject.hostname;
		if (urlObject.port) {
			url += `:${urlObject.port}`;
		}
	}

	// Pathname
	if (urlObject.pathname) {
		url += urlObject.pathname;
	}

	// Query parameters
	if (urlObject.query) {
		const queryParams = new URLSearchParams();
		if (typeof urlObject.query === "string") {
			url += `?${urlObject.query}`;
		} else {
			for (const [key, value] of Object.entries(urlObject.query)) {
				if (value !== undefined && value !== null) {
					if (Array.isArray(value)) {
						for (const item of value) {
							queryParams.append(key, String(item));
						}
					} else {
						queryParams.append(key, String(value));
					}
				}
			}
			const queryString = queryParams.toString();
			if (queryString) {
				url += `?${queryString}`;
			}
		}
	}

	// Hash
	if (urlObject.hash) {
		url += urlObject.hash;
	}

	return url || "/";
}

/**
 * Safely converts any value to a string, avoiding Object's default stringification
 * @param value - The value to convert to string
 * @returns String representation of the value
 */
export function safeToString(value: unknown): string {
	if (value === null) {
		return "null";
	}
	if (value === undefined) {
		return "undefined";
	}
	if (isString(value)) {
		return value;
	}
	if (typeof value === "number" || typeof value === "boolean") {
		return String(value);
	}
	if (value instanceof Date) {
		return value.toISOString();
	}
	// Handle arrays - use JSON.stringify to avoid comma-separated toString
	if (Array.isArray(value)) {
		try {
			return JSON.stringify(value);
		} catch {
			// Fallback for circular references
			return "[object Array]";
		}
	}
	// Handle objects with custom toString first (before checking if it's a plain object)
	if (typeof value === "object" && value !== null && "toString" in value && typeof value.toString === "function") {
		try {
			// eslint-disable-next-line @typescript-eslint/no-base-to-string -- We check the result before using it
			const result = value.toString();
			// Only use toString if it's not the default Object.toString
			if (typeof result === "string" && result !== "[object Object]") {
				return result;
			}
		} catch {
			// Fall through to JSON.stringify
		}
	}
	// Handle plain objects - use JSON.stringify to avoid "[object Object]"
	if (isRecord(value)) {
		try {
			return JSON.stringify(value);
		} catch {
			// Fallback for circular references
			return "[object Object]";
		}
	}
	// Use JSON.stringify for remaining object types
	try {
		return JSON.stringify(value);
	} catch {
		// Fallback for circular references
		return "[object Object]";
	}
}

/**
 * Safely converts a URL value (string or UrlObject) to a string
 * @param value - The URL value to convert (string, UrlObject, or other)
 * @returns String representation of the URL, or empty string for null/undefined
 */
export function safeToStringUrl(value: string | UrlObject | null | undefined): string;
export function safeToStringUrl(value: unknown): string;
export function safeToStringUrl(value: unknown): string {
	if (value === null || value === undefined) {
		return "";
	}
	if (isString(value)) {
		return value;
	}
	if (isUrlObject(value)) {
		try {
			return formatUrlObject(value);
		} catch {
			// Fallback to safeToString if format fails
			return safeToString(value);
		}
	}
	// Fallback for other types
	return safeToString(value);
}

/**
 * Ensures a value is converted to a string, with optional fallback
 * @param value - The value to ensure is a string
 * @param fallback - Optional fallback value if value is null/undefined
 * @returns String representation of the value
 */
export function ensureString(value: unknown, fallback = ""): string {
	if (value === null || value === undefined) {
		return fallback;
	}
	if (isString(value)) {
		return value;
	}
	return safeToString(value);
}
